import { useMemo, useState, useEffect } from 'react';
import heavenMadeLogo from '@/assets/heaven-made-logo.png';
import { CartButton } from './CartProvider';
import ProductCard from './ProductCard';
import { getProductsByDepartment, products } from '@/data/products';
import { useSoldProducts } from '@/hooks/useSoldProducts';
import { useAssetsReady } from '@/hooks/useAssetsReady';


const typewriter = { fontFamily: "'Special Elite', cursive" } as const;

const STORAGE_KEY = 'heavenmade_active_department';

const ImageGallery = () => {
  const [activeDepartment, setActiveDepartment] = useState<'instock' | 'coming'>(() => {
    const saved = typeof window !== 'undefined' ? window.sessionStorage.getItem(STORAGE_KEY) : null;
    return saved === 'coming' ? 'coming' : 'instock';
  });
  const { isSold } = useSoldProducts();

  // Sort so sold items drop to the bottom; within each group, later entries
  // in products.ts are newer uploads, so sort by position descending —
  // newest (e.g. Breton batch) ends up at the bottom.
  const uploadOrder = useMemo(() => {
    const order = new Map<string, number>();
    products.forEach((p, index) => order.set(p.slug, index));
    return order;
  }, []);

  const visibleProducts = useMemo(() => {
    const items = getProductsByDepartment(activeDepartment);
    if (activeDepartment === 'coming') return items;
    return [...items].sort((a, b) => {
      const aSold = isSold(a.slug) ? 1 : 0;
      const bSold = isSold(b.slug) ? 1 : 0;
      if (aSold !== bSold) return aSold - bSold;
      return (uploadOrder.get(b.slug) ?? 0) - (uploadOrder.get(a.slug) ?? 0);
    });
  }, [activeDepartment, isSold, uploadOrder]);

  useEffect(() => {
    window.sessionStorage.setItem(STORAGE_KEY, activeDepartment);
  }, [activeDepartment]);

  // Coming Soon: one product per full screen, snap on page scroll.
  useEffect(() => {
    const root = document.documentElement;
    if (activeDepartment === 'coming') root.classList.add('coming-snap');
    return () => root.classList.remove('coming-snap');
  }, [activeDepartment]);


  // Preload every product's first two images (both tabs) + the logo up front,
  // so nothing pops in with different timings.
  const preloadSources = useMemo(
    () => [heavenMadeLogo, ...products.flatMap((p) => p.images.slice(0, 2).map((i) => i.src))],
    [],
  );
  const ready = useAssetsReady(preloadSources);

  return (
    <div
      className="min-h-screen w-full max-w-full overflow-x-hidden bg-white flex flex-col items-center justify-start pt-8 md:pt-12 px-4 md:px-8 pb-8 relative transition-opacity duration-500 ease-out"
      style={{ opacity: ready ? 1 : 0 }}
    >
      {/* Cart */}
      <div className="absolute top-6 right-6 z-20">
        <CartButton />
      </div>

      {/* Department Navigation */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-6 w-full max-w-4xl mb-8">
        <button
          onClick={() => setActiveDepartment('instock')}
          className={`justify-self-end text-black text-sm md:text-base hover:text-black/70 transition-colors cursor-pointer ${activeDepartment === 'instock' ? 'border-b-2 border-black' : ''}`}
          style={typewriter}
        >
          In Stock
        </button>

        <img
          src={heavenMadeLogo}
          alt="Heaven Made"
          className="h-20 md:h-28"
        />

        <button
          onClick={() => setActiveDepartment('coming')}
          className={`justify-self-start text-black text-sm md:text-base hover:text-black/70 transition-colors cursor-pointer ${activeDepartment === 'coming' ? 'border-b-2 border-black' : ''}`}
          style={typewriter}
        >
          Coming Soon
        </button>
      </div>

      {/* Product Grid */}
      <div className="relative max-w-6xl w-full">
        {visibleProducts.length > 0 ? (
          activeDepartment === 'coming' ? (
            <div className="w-full max-w-4xl mx-auto">
              {visibleProducts.map((product) => (
                <div
                  key={product.slug}
                  className="coming-section flex flex-col items-center justify-center py-4 md:py-8"
                >
                  <ProductCard product={product} large />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-12">
              {visibleProducts.map((product) => (
                <ProductCard key={product.slug} product={product} />
              ))}
            </div>
          )
        ) : (
          <p className="text-black text-sm text-center py-16" style={typewriter}>
            Coming shortly.
          </p>
        )}

      </div>

      {/* Contact Footer */}
      <footer className={`mt-auto w-full max-w-4xl py-12 md:py-16 text-center text-black snap-target`}>
        <h2 className="text-sm md:text-base mb-4" style={typewriter}>
          Contact
        </h2>
        <div className="space-y-1 text-sm md:text-base" style={typewriter}>
          <a href="tel:+46793591098" className="block hover:text-black/70 transition-colors">
            +46 79 359 10 98
          </a>
          <a href="mailto:mio.graaf@gmail.com" className="block hover:text-black/70 transition-colors">
            mio.graaf@gmail.com
          </a>
          <a
            href="https://instagram.com/heavenmade.se"
            target="_blank"
            rel="noopener noreferrer"
            className="block hover:text-black/70 transition-colors"
          >
            @Heavenmade.se
          </a>
          <p className="block">Swedenborgsgatan, Södermalm Stockholm Sweden</p>
        </div>
      </footer>

    </div>
  );
};

export default ImageGallery;
